/**
 * Medição de acessos do site com consentimento (LGPD)
 *
 * O Google Analytics 4 só é ativado quando há um ID configurado (no painel
 * Super Admin ou em VITE_GA_MEASUREMENT_ID) E o visitante aceita os cookies
 * de análise. Sem ID configurado,
 * nada é carregado e nenhum aviso aparece. Até o visitante decidir, nenhum
 * script de terceiro é baixado.
 *
 * A escolha fica no navegador do visitante e pode ser revista a qualquer
 * momento pelo link "Preferências de cookies" do rodapé.
 *
 * Ver docs/seo/09-analytics-e-consentimento.md
 *
 * @author Equipe VendeAi
 * @since 1.4.0
 */

const validId = (id: string | null | undefined) => {
  const v = (id || '').trim();
  return /^G-[A-Z0-9]{4,20}$/.test(v) ? v : '';
};

/** ID definido no build (VITE_GA_MEASUREMENT_ID), usado se o painel não tiver um */
const BUILD_ID = validId(import.meta.env.VITE_GA_MEASUREMENT_ID);

/**
 * ID do GA4 a usar: o do painel Super Admin (vem da API pública) tem
 * prioridade; senão, o do build. Vazio = analytics desligado.
 */
export function resolveMeasurementId(fromPlatform?: string | null): string {
  return validId(fromPlatform) || BUILD_ID;
}

const CONSENT_KEY = 'vendeai:analytics-consent';
export const OPEN_EVENT = 'vendeai:open-cookie-preferences';

export type Consent = 'granted' | 'denied';

interface GtagWindow {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  [key: string]: unknown;
}

export const gtagWindow = () => window as unknown as GtagWindow;

export function readConsent(): Consent | null {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

export function saveConsent(value: Consent) {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Sem armazenamento (modo privado): a escolha vale só para esta visita
  }
}

let loadedId = '';

/** Injeta o gtag.js (uma vez) e configura o GA4 sem page_view automático */
export function loadAnalytics(measurementId: string) {
  const w = gtagWindow();
  w[`ga-disable-${measurementId}`] = false;
  if (loadedId) {
    w.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    if (loadedId !== measurementId) {
      w.gtag?.('config', measurementId, { send_page_view: false });
      loadedId = measurementId;
    }
    return;
  }
  loadedId = measurementId;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // O gtag.js espera o objeto "arguments", não um array
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };
  w.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  w.gtag('js', new Date());
  w.gtag('config', measurementId, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

/** Desliga a coleta quando o visitante retira o consentimento */
export function disableAnalytics(measurementId: string) {
  const w = gtagWindow();
  w[`ga-disable-${measurementId}`] = true;
  w.gtag?.('consent', 'update', { analytics_storage: 'denied' });
}

/** Abre o aviso de cookies novamente (link do rodapé) */
export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Envia um page_view (quando o GA está ativo) */
export function trackPageView(path: string) {
  gtagWindow().gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
